import React, { useMemo } from 'react';
import { Card, Row, Col, Typography, Space, Button } from 'antd';
import styles from '../components/Layout/AboutPage.module.css';
import { useNavigate } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

const AboutPage = () => {
  const navigate = useNavigate();
  // Memoize static data for better performance
  //added usememo
  const features = useMemo(() => [
    {
      title: 'Real-time Metrics',
      description: 'Monitor your AWS infrastructure with comprehensive metrics collection and visualization',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      title: 'Log Analytics',
      description: 'Centralized logging solution for S3 and CloudWatch with advanced search capabilities',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      title: 'Smart Alerts',
      description: 'Intelligent alerting system to proactively identify and resolve issues',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    {
      title: 'Cost Monitoring',
      description: 'Track and optimize your AWS spending with detailed cost analysis',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    },
    {
      title: 'Service Traces',
      description: 'Distributed tracing for microservices to understand system behavior',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    },
    {
      title: 'Clairai-Copilot',
      description: 'Intelligent AI-powered chatbot for answering questions about your platform, infrastructure, and observability data',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
    }
  ], []);

  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <Card className={styles.heroCard}>
        <div className={styles.heroBackground}>
          <Row gutter={[32, 32]} align="middle">
            <Col xs={24} lg={14}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <div className={styles.logoContainer}>
                    <img
                      src="/Logo/LOGO 180X180.svg"
                      alt="ClairAI Logo"
                      className={styles.logo}
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <Title level={1} className={styles.mainTitle} style={{ marginBottom: '0' }}>
                    ClairAI
                  </Title>
                </div>
                
                <Title 
                  level={2} 
                  className={styles.subTitle} 
                  style={{ 
                    marginTop: '16px', 
                    marginBottom: '32px',
                    fontWeight: '500',
                    fontSize: 'clamp(1.25rem, 4vw, 1.75rem)',
                    lineHeight: '1.4',
                    color: 'rgba(255, 255, 255, 0.95)',
                    display: 'inline-block',
                    width: '100%'
                  }}
                >
                  AI Powered Observability for AWS&nbsp;& Databricks
                </Title>
                
                <div style={{ marginBottom: '24px' }}>
                  <Paragraph 
                    className={styles.heroDescription} 
                    style={{ 
                      fontSize: 'clamp(1rem, 2.5vw, 1.125rem)', 
                      lineHeight: '1.75', 
                      marginBottom: '20px',
                      color: 'rgba(255, 255, 255, 0.95)',
                      maxWidth: '100%'
                    }}
                  >
                    Enterprise-grade monitoring platform that delivers instant Root Cause Analysis
                    and actionable recommendations turning hours of debugging into minutes.
                  </Paragraph>
                  
                  <Paragraph 
                    className={styles.heroDescription} 
                    style={{ 
                      fontSize: 'clamp(1rem, 2.5vw, 1.125rem)', 
                      lineHeight: '1.75',
                      marginBottom: '0',
                      color: 'rgba(255, 255, 255, 0.95)',
                      maxWidth: '100%'
                    }}
                  >
                    Get full visibility across your AWS infrastructure and Databricks workloads.
                    ClairAI automatically correlates logs, metrics, and traces to tell you not just
                    what went wrong, but why with precise remediation steps powered by AI.
                  </Paragraph>
                </div>
                
                <div className={styles.buttonContainer} style={{ marginTop: '40px' }}>
                  <Button 
                    type="primary" 
                    size="large"aria-label="Watch 2-Min Demo"
                    className={styles.primaryButton}
                  >
                    Watch 2-Min Demo
                  </Button>
                  <Button 
                    size="large"className={styles.secondaryButton}
                  >
                    Book a Demo / Start Free Trial
                  </Button>
                </div>
              </Space>
            </Col>
            <Col xs={24} lg={10}>
              <div className={styles.companyLogoContainer} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <div className={styles.companyLogoWrapper} style={{ textAlign: 'center', padding: '40px 30px' }}>
                  <img
                    src="/Logo/LOGO 180X180.svg"
                    alt="ClairAI Company Logo"
                    className={styles.companyLogo}
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Card>

      {/* Features Section */}
      <Card className={styles.sectionCard}>
        <Title level={2} className={styles.sectionTitle}>
          
          Platform Capabilities
        </Title>
        <Row gutter={[32, 32]}>
          {features.map((feature, index) => (
            <Col xs={24} md={12} lg={8} key={index}>
              <Card
                hoverable
                className={styles.featureCard}
                style={{ background: feature.gradient }}
                bodyStyle={{ 
                  padding: '32px 24px',
                  background: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(10px)',
                  margin: '3px',
                  borderRadius: '13px',
                  height: 'calc(100% - 6px)',
                  position: 'relative'
                }}
              >
                <div className={styles.featureContent}>
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div className={styles.featureTextContent}>
                      <Title level={4} className={styles.featureTitle}>
                        {feature.title}
                      </Title>
                      <Paragraph className={styles.featureDescription}>
                        {feature.description}
                      </Paragraph>
                    </div>
                  </Space>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Contact & Resources */}
      <Row gutter={[32, 32]}>
        <Col xs={24} md={12}>
          <Card className={styles.contactCard + ' ' + styles.contactCardPrimary}>
            <Title level={3} className={styles.contactTitle}>
              
              Connect With Us
            </Title>
            <Paragraph className={styles.contactDescription}>
              Join our community of developers and DevOps engineers. Get support, share insights, 
              and contribute to the future of observability.
            </Paragraph>
            <Space size="large">
              <Button 
                type="primary"size="large"
                className={styles.contactButton}
                onClick={() => navigate('/contact')}
              >
                Contact Us
              </Button>
              <Button size="large"
                className={styles.contactButtonOutline}
              >
                LinkedIn
              </Button>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card className={styles.contactCard + ' ' + styles.contactCardSecondary}>
            <Title level={3} className={styles.contactTitle}>
              
              Start Your Journey
            </Title>
            <Paragraph className={styles.contactDescription}>
              Ready to transform your AWS monitoring? Get started in minutes with our streamlined 
              onboarding process and see immediate results.
            </Paragraph>
            <Button 
              type="primary" 
              size="large" 
              block
              className={styles.contactButton}
            >
              Onboard Your First Account
            </Button>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default React.memo(AboutPage);